import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangle, IconDownload, IconExternalLink, IconFileTypePdf, IconRefresh } from '@tabler/icons-react';
import axios from 'axios';
import axiosInstance from '@libs/axios';
import { handleCreditBlockError } from '@utils/credit-errors';
import { cleanAjuRaw, formatAjuNumber } from '@utils/aju-formatter';
import { JarvisPrimaryButton, JarvisSecondaryButton } from '@components/jarvis-ui';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';

type DocumentKind = 'final' | 'draft';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading'; refresh: boolean }
  | { status: 'ready'; url: string; kind: DocumentKind; fallback: boolean; fileName: string }
  | { status: 'error'; message: string };

interface DeclarationPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  /** AJU of the row being previewed. Null while nothing is selected. */
  ajuNo: string | null;
}

// Axios hands us a Blob for every response when responseType is 'blob', including
// error bodies. Read it back as JSON so the CEISA message (and the billing error
// code) reach the user instead of a bare "Request failed with status code 400".
const readBlobJson = async (data: unknown): Promise<any | null> => {
  // A real PDF (or anything too large to be an error envelope) is left alone.
  if (!(data instanceof Blob) || data.type === 'application/pdf' || data.size > 64 * 1024) return null;
  try {
    const text = await data.text();
    if (!text.trim()) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const blobErrorMessage = (parsed: any): string | null => parsed?.error?.message || parsed?.message || null;

// Blob URLs the user popped out with "Open in New Tab". That tab still reads
// from the URL, so closing the modal or loading another AJU must not revoke it;
// the browser frees it when this document unloads.
const detachedBlobUrls = new Set<string>();

const fileNameFromDisposition = (header: unknown): string | null => {
  if (typeof header !== 'string') return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Declaration PDF (PIB / PEB) for one Dok Pabean row, without opening the draft.
 *
 * The backend decides which document to serve: the registered (NOPEN) form once
 * the AJU has ever had an NPE / SPPB, otherwise the CEISA draft. It says which
 * one it sent in X-Document-Kind, and X-Document-Fallback when the final could
 * not be fetched and the draft stood in for it.
 *
 * The PDF goes into an iframe over a blob URL so the browser's own viewer
 * provides zoom, thumbnails and print. Every request carries an AbortController:
 * closing the modal, or opening it on another row, cancels the one in flight so
 * a slow CEISA render can never land under the wrong AJU.
 */
export default function DeclarationPreviewModal({ opened, onClose, ajuNo }: DeclarationPreviewModalProps) {
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useLanguage();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [state, setState] = useState<PreviewState>({ status: 'idle' });
  const controllerRef = useRef<AbortController | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const cleanAju = ajuNo ? cleanAjuRaw(ajuNo) : '';
  // BC 2.0 / 3.0 are the only prefixes the spec names; TPB rows (000023, 000027,
  // 000040, …) share the list, so they get a neutral label rather than "PIB".
  const docKind = cleanAju.startsWith('000020') ? 'PIB' : cleanAju.startsWith('000030') ? 'PEB' : null;
  const docLabel = docKind ?? t('docpabean.pdfPreview.docGeneric');
  const fileLabel = docKind ?? 'DOKUMEN';

  const releaseBlobUrl = useCallback(() => {
    const url = blobUrlRef.current;
    blobUrlRef.current = null;
    if (url && !detachedBlobUrls.has(url)) {
      window.URL.revokeObjectURL(url);
    }
  }, []);

  const load = useCallback(
    async (refresh: boolean) => {
      if (!cleanAju) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState({ status: 'loading', refresh });

      try {
        const response = await axiosInstance.get(
          `/api/v1/ceisa/uploads/declaration-preview/${encodeURIComponent(cleanAju)}`,
          {
            params: refresh ? { refresh: true } : undefined,
            responseType: 'blob',
            signal: controller.signal,
          }
        );

        // A 200 carrying a JSON error body would otherwise render as a broken PDF.
        const softError = blobErrorMessage(await readBlobJson(response.data));
        if (controller.signal.aborted) return;
        if (softError) {
          setState({ status: 'error', message: softError });
          return;
        }

        const kind: DocumentKind =
          String(response.headers['x-document-kind'] ?? '').toLowerCase() === 'final' ? 'final' : 'draft';
        const fallback = String(response.headers['x-document-fallback'] ?? '').toLowerCase() === 'true';
        const fileName =
          fileNameFromDisposition(response.headers['content-disposition']) ||
          (kind === 'final'
            ? `${fileLabel}-NOPEN-${cleanAju}.pdf`
            : `draft-${fileLabel.toLowerCase()}-${cleanAju}.pdf`);

        releaseBlobUrl();
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        blobUrlRef.current = url;
        setState({ status: 'ready', url, kind, fallback, fileName });
      } catch (error: any) {
        if (controller.signal.aborted || axios.isCancel(error)) return;

        // Error bodies arrive as Blobs too; parse them so handleCreditBlockError
        // can see the structured error code, not just the HTTP status.
        const parsed = await readBlobJson(error?.response?.data);
        if (controller.signal.aborted) return;
        const normalized = parsed ? { ...error, response: { ...error.response, data: parsed } } : error;

        if (handleCreditBlockError(normalized)) {
          // The persistent billing toast carries the "Bayar Sekarang" link; the
          // modal stays open and says why the preview is empty.
          setState({ status: 'error', message: t('docpabean.pdfPreview.errorBilling') });
          return;
        }

        const message =
          blobErrorMessage(parsed) ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t('docpabean.pdfPreview.errorGeneric');
        setState({ status: 'error', message });
      }
    },
    [cleanAju, fileLabel, releaseBlobUrl, t]
  );

  useEffect(() => {
    if (!opened || !cleanAju) return;
    void load(false);
    // State is left as is here: resetting it now would swap the PDF for the loader
    // while the modal fades out. load() moves straight to 'loading' on the next open.
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      releaseBlobUrl();
    };
    // `load` is keyed on the AJU already; re-running on its identity alone (e.g. a
    // language switch changing `t`) would refetch a PDF that is already on screen.
  }, [opened, cleanAju]);

  // Unmount with the modal still open (e.g. navigating away).
  useEffect(
    () => () => {
      controllerRef.current?.abort();
      releaseBlobUrl();
    },
    [releaseBlobUrl]
  );

  const handleDownload = () => {
    if (state.status !== 'ready') return;
    const link = document.createElement('a');
    link.href = state.url;
    link.setAttribute('download', state.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenInNewTab = () => {
    if (state.status !== 'ready') return;
    detachedBlobUrls.add(state.url);
    window.open(state.url, '_blank', 'noopener,noreferrer');
  };

  const isLoading = state.status === 'loading';
  const ready = state.status === 'ready' ? state : null;

  const kindBadge = ready && (
    <Badge
      size="sm"
      radius="sm"
      variant="light"
      color={ready.kind === 'final' ? 'green' : 'yellow'}
      style={{ flexShrink: 0 }}
    >
      {ready.kind === 'final' ? t('docpabean.pdfPreview.kindFinal') : t('docpabean.pdfPreview.kindDraft')}
    </Badge>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      // Clear the last document once the close animation is over, so the next open
      // never paints a stale badge or a revoked blob URL before load() runs.
      onExitTransitionEnd={() => {
        if (!opened) setState({ status: 'idle' });
      }}
      size="90rem"
      fullScreen={isMobile}
      centered
      radius={isMobile ? 0 : 'md'}
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      title={
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconFileTypePdf size={18} style={{ color: themeColors.red, flexShrink: 0 }} />
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="md" fw={600} truncate style={{ color: themeColors.text }}>
              {t('docpabean.pdfPreview.title').replace('{doc}', docLabel)}
            </Text>
            {cleanAju && (
              <Text size="xs" truncate style={{ color: themeColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                {formatAjuNumber(cleanAju)}
              </Text>
            )}
          </Stack>
          {kindBadge}
        </Group>
      }
      styles={{
        content: {
          background: themeColors.card,
          border: isMobile ? undefined : `1px solid ${themeColors.border}`,
          boxShadow: isDark
            ? '0 20px 40px rgba(0, 0, 0, 0.6)'
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          // Fill Mantine's own cap (100dvh minus the 5dvh offsets) so the flex chain
          // below hands the PDF frame whatever is left after the header, the fallback
          // alert and the button row; the content itself never needs to scroll.
          height: isMobile ? undefined : 'calc(100dvh - 10dvh)',
        },
        header: {
          borderBottom: `1px solid ${themeColors.border}`,
          backgroundColor: themeColors.card,
          flexShrink: 0,
        },
        body: {
          padding: '0.75rem 1rem 1rem',
          backgroundColor: themeColors.card,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        },
        close: { color: themeColors.textSecondary },
      }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        {ready?.fallback && (
          <Alert
            variant="light"
            color="yellow"
            radius="md"
            icon={<IconAlertTriangle size={16} />}
            title={t('docpabean.pdfPreview.fallbackTitle')}
            styles={{ message: { color: themeColors.text } }}
          >
            {t('docpabean.pdfPreview.fallbackBody')}
          </Alert>
        )}

        <Group justify="flex-end" gap="xs" wrap="wrap">
          {(ready?.kind === 'draft' || (state.status === 'loading' && state.refresh)) && (
            <JarvisSecondaryButton
              size="xs"
              radius="md"
              leftIcon={<IconRefresh size={14} />}
              onClick={() => load(true)}
              loading={isLoading}
              disabled={isLoading}
            >
              {t('docpabean.pdfPreview.refreshDraft')}
            </JarvisSecondaryButton>
          )}
          <JarvisSecondaryButton
            size="xs"
            radius="md"
            leftIcon={<IconExternalLink size={14} />}
            onClick={handleOpenInNewTab}
            disabled={!ready}
          >
            {t('docpabean.pdfPreview.openNewTab')}
          </JarvisSecondaryButton>
          <JarvisPrimaryButton
            size="xs"
            radius="md"
            leftIcon={<IconDownload size={14} />}
            onClick={handleDownload}
            disabled={!ready}
          >
            {t('docpabean.pdfPreview.download')}
          </JarvisPrimaryButton>
        </Group>

        <div
          style={{
            flex: 1,
            minHeight: isMobile ? 0 : 240,
            display: 'flex',
            border: `1px solid ${themeColors.border}`,
            borderRadius: 8,
            overflow: 'hidden',
            backgroundColor: themeColors.formFields,
          }}
        >
          {ready ? (
            <iframe
              key={ready.url}
              title={t('docpabean.pdfPreview.title').replace('{doc}', docLabel)}
              src={ready.url}
              style={{ flex: 1, width: '100%', border: 'none', backgroundColor: '#fff' }}
            />
          ) : state.status === 'error' ? (
            <Stack align="center" justify="center" gap="sm" p="lg" style={{ flex: 1, textAlign: 'center' }}>
              <IconAlertTriangle size={28} style={{ color: themeColors.red }} />
              <Text size="sm" fw={600} style={{ color: themeColors.text }}>
                {t('docpabean.pdfPreview.errorTitle')}
              </Text>
              <Text size="xs" maw={480} style={{ color: themeColors.textSecondary }}>
                {state.message}
              </Text>
              <JarvisSecondaryButton
                size="xs"
                radius="md"
                leftIcon={<IconRefresh size={14} />}
                onClick={() => load(false)}
              >
                {t('docpabean.pdfPreview.retry')}
              </JarvisSecondaryButton>
            </Stack>
          ) : (
            <Stack align="center" justify="center" gap="sm" p="lg" style={{ flex: 1, textAlign: 'center' }}>
              <Loader size="md" color={themeColors.accent} />
              <Text size="sm" fw={600} style={{ color: themeColors.text }}>
                {t('docpabean.pdfPreview.loading')}
              </Text>
              <Text size="xs" maw={420} style={{ color: themeColors.textSecondary }}>
                {t('docpabean.pdfPreview.loadingHint')}
              </Text>
            </Stack>
          )}
        </div>
      </Stack>
    </Modal>
  );
}
